import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MessageCircle, Send, Loader2, RefreshCw, X } from 'lucide-react';
import { formatBackendDateTime } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

/**
 * Dedicated Live Support admin page.
 * Separate from Compliance Hub per UX request.
 * Lists open worker-initiated chats (from the movable ? in worker app).
 * Click to open live thread with real-time WS updates + reply capability.
 */
export default function SupportPage() {
  const { token, isAdmin } = useAuth();

  const [supportChats, setSupportChats] = useState([]);
  const [supportLoading, setSupportLoading] = useState(true);
  const [supportLoadError, setSupportLoadError] = useState(null);
  const [selectedSupportChat, setSelectedSupportChat] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportReply, setSupportReply] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportTab, setSupportTab] = useState('staff'); // 'staff' | 'public'

  const wsRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const fetchSupportChats = async () => {
    setSupportLoading(true);
    setSupportLoadError(null);
    try {
      const res = await fetch(`${API}/admin/support-chats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load support chats (status ${res.status})`);
      const data = await res.json();
      setSupportChats(Array.isArray(data?.chats) ? data.chats : []);
    } catch (e) {
      console.error(e);
      setSupportLoadError(e.message || 'Network or CORS error');
      toast.error('Could not load live support chats (see console for details)');
      setSupportChats([]);
    } finally {
      setSupportLoading(false);
    }
  };

  const connectWS = (chatId) => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (!chatId || !token) return;

    const wsUrl = `${API.replace('http', 'ws')}/ws/support-chat/${chatId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[Support] WS connected for chat', chatId);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'message' && data.message) {
          setSupportMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      } catch (e) {
        /* ignore */
      }
    };

    ws.onclose = () => {
      if (wsRef.current === ws) wsRef.current = null;
    };

    ws.onerror = (err) => {
      console.warn('[Support] WS error', err);
    };
  };

  const disconnectWS = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const openSupportThread = async (chat) => {
    setSelectedSupportChat(chat);
    setSupportMessages([]);
    setSupportReply('');

    // Load history via dedicated admin endpoint.
    // The messages response now also includes "chat" with any worker_name / worker_employee_number snapshot from the doc
    // so the thread header + message labels can show real name/EMP even if the queue list was from an older backend deploy.
    try {
      const res = await fetch(`${API}/admin/support-chats/${chat.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSupportMessages(Array.isArray(data?.messages) ? data.messages : []);
        if (data?.chat) {
          // Merge any better worker info (name, emp#, id) from the chat doc snapshot into the selected item for title/labels.
          setSelectedSupportChat((prev) => {
            const base = prev || chat || {};
            const extra = data.chat || {};
            return {
              ...base,
              ...extra,
              worker_name: extra.worker_name || base.worker_name,
              worker_employee_number: extra.worker_employee_number || base.worker_employee_number,
              worker_id: extra.worker_id || base.worker_id,
            };
          });
        }
      }
    } catch (e) {
      console.error('Failed to load thread messages', e);
    }

    // Connect for live push (worker messages + any admin echoes)
    connectWS(chat.id);
  };

  const closeSupportThread = () => {
    disconnectWS();
    setSelectedSupportChat(null);
    setSupportMessages([]);
    setSupportReply('');
    // Refresh the queue so unread counts update
    fetchSupportChats();
  };

  const sendSupportReply = async () => {
    const text = supportReply.trim();
    if (!text || !selectedSupportChat) return;

    setSupportSending(true);
    const chatId = selectedSupportChat.id;

    // Optimistic UI (will be removed on success; WS broadcast delivers the real authoritative message with proper id/sender_name)
    const optimistic = {
      id: 'temp-' + Date.now(),
      from: 'admin',
      text,
      created_at: new Date().toISOString(),
    };
    setSupportMessages((prev) => [...prev, optimistic]);
    setSupportReply('');

    try {
      const res = await fetch(`${API}/admin/support-chats/${chatId}/reply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) {
        throw new Error('Reply failed');
      }
      // Remove the temp optimistic immediately — the connected WS will receive the broadcast with the real DB id and append the authoritative version (with sender_name etc.).
      setSupportMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      toast.success('Reply sent');
    } catch (e) {
      console.error(e);
      toast.error('Failed to send reply');
      // rollback optimistic on failure
      setSupportMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setSupportReply(text);
    } finally {
      setSupportSending(false);
    }
  };

  // Initial load + periodic refresh of the queue list while page is open
  useEffect(() => {
    fetchSupportChats();
    const interval = setInterval(() => {
      if (!selectedSupportChat) {
        fetchSupportChats();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedSupportChat]);

  // Cleanup WS on unmount
  useEffect(() => {
    return () => disconnectWS();
  }, []);

  if (!isAdmin || (typeof isAdmin === 'function' && !isAdmin())) {
    return (
      <div className="p-8 text-center text-text-muted">
        This page is restricted to administrators and managers.
      </div>
    );
  }

  const staffChats = supportChats.filter((c) => c.source !== 'public');
  const publicChats = supportChats.filter((c) => c.source === 'public');
  const visibleChats = supportTab === 'public' ? publicChats : staffChats;

  return (
    <div className="min-h-screen bg-[#F8FAFA] p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-semibold flex items-center gap-3">
              <MessageCircle className="h-6 w-6" /> Live Support
            </h1>
            <p className="text-sm text-text-muted mt-1">
              Real-time chats from workers and public website enquiries.
            </p>
          </div>
          <Button variant="outline" onClick={fetchSupportChats} disabled={supportLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${supportLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Source Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
          <button
            onClick={() => setSupportTab('staff')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              supportTab === 'staff'
                ? 'bg-white shadow-sm text-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Staff Chats
            {staffChats.length > 0 && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {staffChats.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setSupportTab('public')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              supportTab === 'public'
                ? 'bg-white shadow-sm text-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Public Enquiries
            {publicChats.length > 0 && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                {publicChats.length}
              </span>
            )}
          </button>
        </div>

        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {supportTab === 'public' ? 'Public Enquiries' : 'Staff Chats'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {supportLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : supportLoadError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                <p className="font-medium text-red-700">Failed to load support chats</p>
                <p className="text-sm text-red-600 mt-1">{supportLoadError}</p>
                <p className="text-xs text-red-600 mt-2">This is usually a CORS or network issue (see the errors in the browser console). Make sure the backend has been redeployed with the latest security.py (CORS list includes https://app.osabeacares.co.uk) and the worker live chat send fix.</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={fetchSupportChats}>Try again</Button>
              </div>
            ) : visibleChats.length === 0 ? (
              <div className="text-center py-12 text-text-muted">
                {supportTab === 'public'
                  ? 'No public enquiries yet. These appear when a website visitor uses the chat widget.'
                  : 'No open staff chats at the moment. Workers can start a chat using the floating ? button in their dashboard.'}
              </div>
            ) : (
              <div className="space-y-3">
                {visibleChats.map((chat) => (
                  <div
                    key={chat.id}
                    className="flex items-center justify-between rounded-xl border p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        {chat.source === 'public' ? (
                          <>
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Public</span>
                            {chat.contact_name || 'Website Visitor'}
                          </>
                        ) : (
                          (() => {
                            const wn = chat.worker_name;
                            if (wn && !wn.startsWith('Worker ')) return wn;
                            const emp = chat.worker_employee_number;
                            if (emp && !String(emp).match(/^[0-9a-f]{5,}$/i)) return `Worker (${emp})`;
                            return 'Worker';
                          })()
                        )}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5">
                        {chat.source === 'public' ? (
                          <>
                            {chat.contact_email ? `${chat.contact_email} • ` : ''}
                            {chat.contact_phone ? `${chat.contact_phone} • ` : ''}
                            Last activity: {formatBackendDateTime(chat.last_activity)}
                          </>
                        ) : (
                          <>
                            {(() => {
                              const emp = chat.worker_employee_number;
                              const isHex = emp && String(emp).match(/^[0-9a-f]{5,}$/i);
                              const empDisplay = emp && !isHex ? emp : null;
                              const widShort = (chat.worker_id || chat.id || '').slice(0, 8);
                              return empDisplay ? `Emp #: ${empDisplay}` : `Worker ref: ${widShort}`;
                            })()}
                            {chat.worker_email ? ` • ${chat.worker_email}` : ''}
                            {' • '}Last activity: {formatBackendDateTime(chat.last_activity)}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {chat.unread > 0 && (
                        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                          {chat.unread} new
                        </span>
                      )}
                      <Button size="sm" onClick={() => openSupportThread(chat)}>
                        Open Thread
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live Thread Dialog */}
        <Dialog open={!!selectedSupportChat} onOpenChange={(open) => !open && closeSupportThread()}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Support Thread with {(() => {
                  const wn = selectedSupportChat?.worker_name;
                  if (wn && !wn.startsWith('Worker ')) return wn;
                  const emp = selectedSupportChat?.worker_employee_number;
                  if (emp && !String(emp).match(/^[0-9a-f]{5,}$/i)) return `Worker (${emp})`;
                  return 'Worker';
                })()}</span>
                <button onClick={closeSupportThread} className="text-text-muted hover:text-text-primary">
                  <X className="h-4 w-4" />
                </button>
              </DialogTitle>
              <p className="text-xs text-text-muted -mt-1">Live updates via WebSocket • Worker can see replies instantly</p>
              {selectedSupportChat?.worker_employee_number && !String(selectedSupportChat.worker_employee_number).match(/^[0-9a-f]{5,}$/i) && (
                <p className="text-[10px] text-text-muted mt-0.5">
                  Emp #: {selectedSupportChat.worker_employee_number} • From worker’s dashboard (linked to their employee profile)
                </p>
              )}
            </DialogHeader>

            <div className="flex-1 min-h-[320px] max-h-[420px] overflow-y-auto space-y-3 rounded-lg border bg-muted/10 p-4 text-sm">
              {supportMessages.length === 0 ? (
                <p className="text-center text-text-muted py-8 text-xs">No messages in this thread yet.</p>
              ) : (
                supportMessages.map((m, idx) => (
                  <div key={m.id || idx} className={`flex ${m.from === 'admin' ? 'justify-end' : ''}`}>
                    <div
                      className={`max-w-[82%] rounded-2xl px-3 py-2 ${
                        m.from === 'admin' ? 'bg-primary text-primary-foreground' : 'bg-white border'
                      }`}
                    >
                      <div className="text-[10px] opacity-70 mb-0.5 flex items-center gap-1">
                        {(() => {
                          if (m.from === 'admin') return m.from + (m.admin_id ? ' (you)' : '');
                          // For public chats, use contact_name
                          if (selectedSupportChat?.source === 'public') {
                            return selectedSupportChat?.contact_name || 'Visitor';
                          }
                          // For worker messages, prefer a clean name or EMP code from the chat metadata (the enriched list item)
                          // This prevents leaking technical worker_id hex or chat.id in labels for old messages.
                          const wn = selectedSupportChat?.worker_name;
                          if (wn && !wn.startsWith('Worker ')) return wn;
                          const emp = selectedSupportChat?.worker_employee_number;
                          if (emp && !String(emp).match(/^[0-9a-f]{5,}$/i)) return `Worker (${emp})`;
                          // Fall back to whatever was stored on the message at send time, or generic
                          const sn = m.sender_name;
                          if (sn && !String(sn).match(/^[0-9a-f]{5,}$/i) && !sn.startsWith('Worker ')) return sn;
                          return 'Worker';
                        })()}
                      </div>
                      <div>{m.text}</div>
                      <div className="text-[10px] opacity-60 mt-1 text-right">
                        {m.created_at ? formatBackendDateTime(m.created_at) : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {supportSending && <div className="text-xs text-text-muted pl-1">Sending…</div>}
            </div>

            <div className="border-t pt-3">
              <div className="flex gap-2">
                <Textarea
                  value={supportReply}
                  onChange={(e) => setSupportReply(e.target.value)}
                  placeholder="Type your reply to the worker..."
                  className="min-h-[64px] flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendSupportReply();
                    }
                  }}
                  disabled={supportSending}
                />
                <Button
                  onClick={sendSupportReply}
                  disabled={supportSending || !supportReply.trim()}
                  className="self-end"
                  size="sm"
                >
                  {supportSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-center text-text-muted mt-2">
                Press Enter to send • Shift+Enter for newline
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
